import { DFPEmpty } from "@/components/empty/dfp-empty";
import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { usePredspendCache } from "@/hooks/use-predspend-cache";
import { Loader } from "@/layouts/signed-in/Loader/Loader";
import type { ProjectedSpendingChartData } from "@/models/organization/dashboard-analytics/dashboard-analytics.model";
import { auth } from "@/services/auth-service";
import { useUserStore } from "@/stores";
import { UserAccessLevel } from "@/stores/user-store";
import { PREDSPEND_CACHE_KEYS, predspendCache } from "@/utils/predspend-cache";
import {
	BarChartOutlined,
	DollarOutlined,
	DownloadOutlined,
	ReloadOutlined,
	SettingOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	Radio,
	Row,
	Select,
	Space,
	Statistic,
	Switch,
	message,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AssetBreakdown } from "./childFunctions/AssetBreakdown";
import { PredSpendChart } from "./childFunctions/PredSpendChart";
import { PredictiveSpendSettingsModal } from "./components/PredictiveSpendSettingsModal";
import "./PredSpendTab.scss";

// ===================================
// TYPE DEFINITIONS
// ===================================

interface DashboardAssetAnalytics {
	healthScore: number | null;
	totalBudget: number | null;
	dollarAmountPastUsefulLife: number | null;
}

interface CoreDashboardAnalytics {
	analytics: DashboardAssetAnalytics;
	projectedSpendingChartData: ProjectedSpendingChartData;
}

type ReplacementStrategy =
	| "first-in-first-out"
	| "economics-of-scale"
	| "extended-replacement";
type YearView = 5 | 10;

// Breakdown state interface
interface BreakdownData {
	year: string;
	assetType: string;
	totalCost: number;
	totalCount: number;
}

// ===================================
// MAIN COMPONENT
// ===================================

/**
 * Predictive Spend Tab - Asset Replacement Planning
 *
 * Features:
 * - Overview analytics cards showing total budget, past useful life assets, health score
 * - Interactive chart showing projected spending by replacement year and asset type
 * - Toggle between cost view and asset count view
 * - Year view filtering (5, 10, 15 years)
 * - Replacement strategy selection
 * - Export functionality for budget planning
 */
const PredSpendTab = () => {
	// ===================================
	// HOOKS & STATE
	// ===================================

	const { user } = useUserStore();
	const navigate = useNavigate();

	// Initialize predspend cache management
	const { getCurrentOrgId } = usePredspendCache();

	// Core application state
	const [loading, setLoading] = useState(true);
	const [dashboardData, setDashboardData] =
		useState<CoreDashboardAnalytics | null>(null);
	const [isYAxisReplCost, setIsYAxisReplCost] = useState(true);
	const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

	// New state for filtering and strategy
	const [yearView, setYearView] = useState<YearView>(10);
	const [replacementStrategy, setReplacementStrategy] =
		useState<ReplacementStrategy>("first-in-first-out");

	// Breakdown view state
	const [showBreakdown, setShowBreakdown] = useState(false);
	const [breakdownData, setBreakdownData] = useState<BreakdownData | null>(
		null,
	);

	// Settings modal state
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

	// ===================================
	// COMPUTED DATA
	// ===================================

	/**
	 * Filter and transform chart data based on selected year view and replacement strategy
	 */
	const processedChartData = useMemo(() => {
		if (!dashboardData?.projectedSpendingChartData) return null;

		const originalData = dashboardData.projectedSpendingChartData;
		const currentYear = new Date().getFullYear();
		const maxYear = currentYear + yearView;

		// Filter years based on selected view
		const filteredYears = originalData.yearsWithAssets.filter(
			(year) => parseInt(year) <= maxYear,
		);

		// Apply replacement strategy
		let processedData = { ...originalData };

		switch (replacementStrategy) {
			case "first-in-first-out":
				// Default behavior - no changes needed
				break;

			case "economics-of-scale":
				// Group similar asset types in consecutive years for bulk purchasing
				processedData = applyEconomicsOfScaleStrategy(
					originalData,
					filteredYears,
				);
				break;

			case "extended-replacement":
				// Only replace critical assets each year, defer non-critical replacements
				processedData = applyExtendedReplacementStrategy(
					originalData,
					filteredYears,
				);
				break;
		}

		// Filter the processed data to the selected year range
		const filteredAssetValuesByYearAndType: Record<
			string,
			Record<string, number>
		> = {};
		const filteredAssetCountByYearAndType: Record<
			string,
			Record<string, number>
		> = {};

		filteredYears.forEach((year) => {
			filteredAssetValuesByYearAndType[year] =
				processedData.assetValuesByYearAndType[year] || {};
			filteredAssetCountByYearAndType[year] =
				processedData.assetCountByYearAndType[year] || {};
		});

		// Recalculate total budget for filtered years
		let filteredTotalBudget = 0;
		filteredYears.forEach((year) => {
			Object.values(filteredAssetValuesByYearAndType[year] || {}).forEach(
				(value) => {
					filteredTotalBudget += Number(value);
				},
			);
		});

		return {
			...processedData,
			yearsWithAssets: filteredYears,
			assetValuesByYearAndType: filteredAssetValuesByYearAndType,
			assetCountByYearAndType: filteredAssetCountByYearAndType,
			totalBudget: filteredTotalBudget,
		};
	}, [dashboardData, yearView, replacementStrategy]);

	// ===================================
	// STRATEGY FUNCTIONS
	// ===================================

	/**
	 * Apply economics of scale strategy - group similar asset replacements
	 */
	const applyEconomicsOfScaleStrategy = (
		data: ProjectedSpendingChartData,
		years: string[],
	): ProjectedSpendingChartData => {
		const newAssetValuesByYearAndType = { ...data.assetValuesByYearAndType };
		const newAssetCountByYearAndType = { ...data.assetCountByYearAndType };

		// Group consecutive years with small quantities into larger batches
		data.assetTypes.forEach((assetType) => {
			const assetReplacements: Array<{
				year: string;
				cost: number;
				count: number;
			}> = [];

			years.forEach((year) => {
				const cost = data.assetValuesByYearAndType[year]?.[assetType] || 0;
				const count = data.assetCountByYearAndType[year]?.[assetType] || 0;
				if (cost > 0 || count > 0) {
					assetReplacements.push({ year, cost, count });
				}
			});

			// If there are small batches in consecutive years, combine them
			for (let i = 0; i < assetReplacements.length - 1; i++) {
				const current = assetReplacements[i];
				const next = assetReplacements[i + 1];

				// If both batches are small (< 10 units) and within 2 years, combine them
				if (
					current.count < 10 &&
					next.count < 10 &&
					parseInt(next.year) - parseInt(current.year) <= 2
				) {
					// Move next year's replacements to current year
					newAssetValuesByYearAndType[current.year] =
						newAssetValuesByYearAndType[current.year] || {};
					newAssetCountByYearAndType[current.year] =
						newAssetCountByYearAndType[current.year] || {};

					newAssetValuesByYearAndType[current.year][assetType] =
						(newAssetValuesByYearAndType[current.year][assetType] || 0) +
						next.cost;
					newAssetCountByYearAndType[current.year][assetType] =
						(newAssetCountByYearAndType[current.year][assetType] || 0) +
						next.count;

					// Clear next year
					if (newAssetValuesByYearAndType[next.year]) {
						newAssetValuesByYearAndType[next.year][assetType] = 0;
					}
					if (newAssetCountByYearAndType[next.year]) {
						newAssetCountByYearAndType[next.year][assetType] = 0;
					}
				}
			}
		});

		return {
			...data,
			assetValuesByYearAndType: newAssetValuesByYearAndType,
			assetCountByYearAndType: newAssetCountByYearAndType,
		};
	};

	/**
	 * Apply extended replacement strategy - only replace critical assets
	 * Respects extendedLifeEligible flag from settings
	 */
	const applyExtendedReplacementStrategy = (
		data: ProjectedSpendingChartData,
		years: string[],
	): ProjectedSpendingChartData => {
		const newAssetValuesByYearAndType = { ...data.assetValuesByYearAndType };
		const newAssetCountByYearAndType = { ...data.assetCountByYearAndType };

		// Get eligible asset types from backend (based on extendedLifeEligible flag)
		const eligibleTypes = new Set(data.extendedLifeEligibleTypes || []);

		// Defer replacements for eligible asset types
		// Non-eligible types are treated as critical and replaced on schedule
		const deferredReplacements: Record<
			string,
			{ cost: number; count: number }
		> = {};

		years.forEach((year) => {
			data.assetTypes.forEach((assetType) => {
				const originalCost =
					data.assetValuesByYearAndType[year]?.[assetType] || 0;
				const originalCount =
					data.assetCountByYearAndType[year]?.[assetType] || 0;

				if (originalCost > 0 && originalCount > 0) {
					// Check if this asset type is eligible for extended life
					if (eligibleTypes.has(assetType)) {
						// Eligible types: Keep 40% as critical, defer 60%
						const criticalCost = Math.round(originalCost * 0.4);
						const criticalCount = Math.round(originalCount * 0.4);
						const deferredCost = originalCost - criticalCost;
						const deferredCount = originalCount - criticalCount;

						// Set critical replacements for this year
						newAssetValuesByYearAndType[year] =
							newAssetValuesByYearAndType[year] || {};
						newAssetCountByYearAndType[year] =
							newAssetCountByYearAndType[year] || {};
						newAssetValuesByYearAndType[year][assetType] = criticalCost;
						newAssetCountByYearAndType[year][assetType] = criticalCount;

						// Accumulate deferred replacements
						if (deferredCost > 0 && deferredCount > 0) {
							deferredReplacements[assetType] = deferredReplacements[
								assetType
							] || { cost: 0, count: 0 };
							deferredReplacements[assetType].cost += deferredCost;
							deferredReplacements[assetType].count += deferredCount;
						}
					} else {
						// Non-eligible types: Keep full replacement schedule (100% critical)
						newAssetValuesByYearAndType[year] =
							newAssetValuesByYearAndType[year] || {};
						newAssetCountByYearAndType[year] =
							newAssetCountByYearAndType[year] || {};
						newAssetValuesByYearAndType[year][assetType] = originalCost;
						newAssetCountByYearAndType[year][assetType] = originalCount;
					}
				}
			});
		});

		// Distribute deferred replacements to the last years in the view
		const lastYear = years.reduce(
			(max, year) => (parseInt(year) > parseInt(max) ? year : max),
			years[0],
		);
		Object.entries(deferredReplacements).forEach(([assetType, deferred]) => {
			newAssetValuesByYearAndType[lastYear] =
				newAssetValuesByYearAndType[lastYear] || {};
			newAssetCountByYearAndType[lastYear] =
				newAssetCountByYearAndType[lastYear] || {};
			newAssetValuesByYearAndType[lastYear][assetType] =
				(newAssetValuesByYearAndType[lastYear][assetType] || 0) + deferred.cost;
			newAssetCountByYearAndType[lastYear][assetType] =
				(newAssetCountByYearAndType[lastYear][assetType] || 0) + deferred.count;
		});

		return {
			...data,
			assetValuesByYearAndType: newAssetValuesByYearAndType,
			assetCountByYearAndType: newAssetCountByYearAndType,
		};
	};

	// ===================================
	// EFFECTS
	// ===================================

	/**
	 * Initialize component - fetch dashboard data when organization is available
	 */
	useEffect(() => {
		if (user?.organization?.id) {
			fetchDashboardData();
		}
	}, [user?.organization?.id]);

	// ===================================
	// API FUNCTIONS
	// ===================================

	/**
	 * Fetch dashboard analytics data including projected spending chart data
	 * Endpoint: /core/organization/dashboard
	 */
	const fetchDashboardData = async (forceRefresh: boolean = false) => {
		if (!user?.organization?.id) return;

		try {
			setLoading(true);

			// Check cache first (skip if force refresh)
			if (!forceRefresh) {
				const cachedData = predspendCache.get<CoreDashboardAnalytics>(
					user.organization.id,
					PREDSPEND_CACHE_KEYS.ANALYTICS,
					{
						yearView,
						replacementStrategy,
					},
				);

				if (cachedData) {
					setDashboardData(cachedData);
					setLastRefreshed(new Date()); // Update refresh timestamp
					setLoading(false);
					return;
				}
			}

			// Show progress message after 5 seconds
			const progressTimeout = setTimeout(() => {
				message.info("Loading asset replacement data...");
			}, 5000);

			const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/dashboard`;
			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			});

			clearTimeout(progressTimeout);

			if (!response.ok) {
				throw new Error(
					`Failed to fetch dashboard analytics (${response.status})`,
				);
			}

			const result = await response.json();

			// Extract data from the API response format
			const data: CoreDashboardAnalytics = result.success
				? result.data
				: result;

			// Cache the data
			predspendCache.set(
				user.organization.id,
				PREDSPEND_CACHE_KEYS.ANALYTICS,
				data,
				{
					yearView,
					replacementStrategy,
				},
			);

			setDashboardData(data);
			setLastRefreshed(new Date());
		} catch (error: unknown) {
			console.error("Failed to fetch dashboard analytics:", error);

			if (error instanceof Error) {
				message.error(
					"Failed to load asset replacement data. Please try again.",
				);
			} else {
				message.error(
					"Failed to load asset replacement data. Please try again.",
				);
			}

			setDashboardData(null);
		} finally {
			setLoading(false);
		}
	};

	// ===================================
	// EVENT HANDLERS
	// ===================================

	/**
	 * Refresh dashboard data
	 */
	const refreshData = async () => {
		await fetchDashboardData(true); // Force refresh
		message.success("Data refreshed successfully");
	};

	/**
	 * Handle column click to show breakdown
	 */
	const handleColumnClick = (
		year: string,
		assetType: string,
		cost: number,
		count: number,
	) => {
		setBreakdownData({
			year: String(year),
			assetType,
			totalCost: cost,
			totalCount: count,
		});
		setShowBreakdown(true);
	};

	/**
	 * Handle back navigation from breakdown to main view
	 */
	const handleBackToOverview = () => {
		setShowBreakdown(false);
		setBreakdownData(null);
	};

	/**
	 * Export prediction data for budget planning
	 */
	const exportData = async () => {
		if (!processedChartData) {
			message.error("No data available to export");
			return;
		}

		try {
			// Create CSV data from projected spending (using processed data)
			const {
				yearsWithAssets,
				assetTypes,
				assetValuesByYearAndType,
				assetCountByYearAndType,
			} = processedChartData;

			const csvRows = [
				`Year,Asset Type,Projected Cost,Asset Count,Strategy,Year View`,
			];

			yearsWithAssets.forEach((year) => {
				assetTypes.forEach((assetType) => {
					const cost = assetValuesByYearAndType[year]?.[assetType] || 0;
					const count = assetCountByYearAndType[year]?.[assetType] || 0;
					if (cost > 0 || count > 0) {
						csvRows.push(
							`${year},${assetType},${cost},${count},${replacementStrategy},${yearView} years`,
						);
					}
				});
			});

			// Download CSV
			const csvContent = csvRows.join("\n");
			const blob = new Blob([csvContent], { type: "text/csv" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `asset_replacement_plan_${replacementStrategy}_${yearView}yr_${new Date().getFullYear()}.csv`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);

			message.success("Asset replacement plan exported successfully");
		} catch (error) {
			console.error("Export error:", error);
			message.error("Failed to export data");
		}
	};

	// ===================================
	// UTILITY FUNCTIONS
	// ===================================

	/**
	 * Check if current user is Super Admin
	 */
	const isSuperAdmin = () => {
		const accessLevel =
			user?.role?.accessLevel ?? user?.role?.access_level ?? 0;
		return accessLevel >= UserAccessLevel.SUPER_ADMIN;
	};

	/**
	 * Handle settings updated callback
	 */
	const handleSettingsUpdated = async () => {
		if (!user?.organization?.id) return;

		// Clear all cached data for this organization
		predspendCache.invalidateOrganization(user.organization.id);

		// Show loading message
		message.loading("Applying new settings...", 0.5);

		// Small delay to ensure backend has processed the settings
		await new Promise((resolve) => setTimeout(resolve, 500));

		// Force refresh data with new settings
		await fetchDashboardData(true);

		message.success("Settings applied successfully!");
	};

	/**
	 * Format number as currency (USD)
	 */
	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	/**
	 * Get health score color based on value
	 */
	const getHealthColor = (score: number) => {
		if (score >= 80) return "#52c41a";
		if (score >= 60) return "#faad14";
		return "#ff4d4f";
	};

	// ===================================
	// RENDER CONDITIONS
	// ===================================

	// Show loading spinner while fetching data
	if (loading && !dashboardData) {
		return <Loader />;
	}

	// Show error if no organization access
	if (!user?.organization?.id) {
		return (
			<DFPEmpty description="Please ensure you have proper organization access." />
		);
	}

	// ===================================
	// RENDER JSX
	// ===================================

	// ===================================
	// CONDITIONAL RENDERING
	// ===================================

	// Show breakdown view if selected
	if (showBreakdown && breakdownData) {
		return (
			<div className="pred-spend-tab">
				<AssetBreakdown
					year={breakdownData.year}
					assetType={breakdownData.assetType}
					totalCost={breakdownData.totalCost}
					totalCount={breakdownData.totalCount}
					onBack={handleBackToOverview}
					organizationId={String(user?.organization?.id || "")}
					isYAxisReplCost={isYAxisReplCost}
				/>
			</div>
		);
	}

	return (
		<div className="pred-spend-tab">
			{/* =============== HEADER SECTION =============== */}
			<div className="pred-spend-header">
				<div className="header-content">
					<div className="header-title">
						<h2>Asset Replacement Planning</h2>
						<p>
							Strategic planning for asset replacement based on condition,
							useful life, and replacement timelines
						</p>
					</div>
					<div className="header-actions">
						<Space>
							{isSuperAdmin() && (
								<>
									<Button
										icon={<BarChartOutlined />}
										onClick={() => navigate("/budget-simulation")}
										type="primary"
									>
										Budget Simulation
									</Button>
									<Button
										icon={<SettingOutlined />}
										onClick={() => setIsSettingsModalOpen(true)}
										type="default"
									>
										Settings
									</Button>
								</>
							)}
							<Button
								icon={<ReloadOutlined />}
								onClick={refreshData}
								loading={loading}
							>
								Refresh
							</Button>
							<Button
								icon={<DownloadOutlined />}
								onClick={exportData}
								disabled={!processedChartData}
							>
								Export Plan
							</Button>
						</Space>
					</div>
				</div>
				{lastRefreshed && (
					<div className="last-updated">
						Last updated: {lastRefreshed.toLocaleDateString()} at{" "}
						{lastRefreshed.toLocaleTimeString()}
					</div>
				)}
			</div>

			{/* =============== ANALYTICS CARDS SECTION =============== */}
			{dashboardData ? (
				<div className="analytics-cards">
					<Row gutter={[16, 16]}>
						{/* Total Budget Card */}
						<Col xs={24} sm={8}>
							<Card className="analytics-card">
								<Statistic
									title="Total Replacement Budget"
									value={
										processedChartData?.totalBudget ||
										dashboardData.projectedSpendingChartData.totalBudget
									}
									formatter={(value) => formatCurrency(Number(value))}
									prefix={<DollarOutlined />}
								/>
								<div className="card-footer">
									<span className="subtitle">
										{yearView}-year projected replacement cost
									</span>
								</div>
							</Card>
						</Col>

						{/* Past Useful Life Assets Card */}
						<Col xs={24} sm={8}>
							<Card className="analytics-card">
								<Statistic
									title="Past Useful Life"
									value={
										dashboardData.analytics.dollarAmountPastUsefulLife || 0
									}
									formatter={(value) => formatCurrency(Number(value))}
									prefix={<WarningOutlined />}
									valueStyle={{ color: "#ff4d4f" }}
								/>
								<div className="card-footer">
									<span className="subtitle">
										Assets requiring immediate attention
									</span>
								</div>
							</Card>
						</Col>

						{/* Overall Health Score Card */}
						<Col xs={24} sm={8}>
							<Card className="analytics-card">
								<Statistic
									title="Overall Health Score"
									value={dashboardData.analytics.healthScore || 0}
									formatter={(value) => `${Number(value).toFixed(1)}%`}
									prefix={<BarChartOutlined />}
									valueStyle={{
										color: getHealthColor(
											dashboardData.analytics.healthScore || 0,
										),
									}}
								/>
								<div className="card-footer">
									<span className="subtitle">
										Average asset condition score
									</span>
								</div>
							</Card>
						</Col>
					</Row>
				</div>
			) : (
				<div className="analytics-cards">
					<Card>
						<DFPEmpty description="No asset data available for replacement planning" />
					</Card>
				</div>
			)}

			{/* =============== CHART SECTION =============== */}
			{processedChartData ? (
				<div
					className="chart-section"
					style={{ paddingBottom: "16px", marginBottom: "12px" }}
				>
					<Card>
						<div className="chart-header" style={{ marginBottom: "16px" }}>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "flex-start",
									flexWrap: "wrap",
									gap: "16px",
								}}
							>
								<h3 style={{ margin: 0 }}>Projected Asset Replacements</h3>
								<div
									style={{
										display: "flex",
										gap: "16px",
										alignItems: "center",
										flexWrap: "wrap",
									}}
								>
									{/* Year View Toggle */}
									<Space>
										<span>Year View:</span>
										<Radio.Group
											value={yearView}
											onChange={(e) => setYearView(e.target.value)}
											size="small"
										>
											<Radio.Button value={5}>5 Years</Radio.Button>
											<Radio.Button value={10}>10 Years</Radio.Button>
										</Radio.Group>
									</Space>

									{/* Strategy Dropdown */}
									<Space>
										<span>Strategy:</span>
										<Select
											value={replacementStrategy}
											onChange={setReplacementStrategy}
											style={{ width: 200 }}
											size="small"
										>
											<Select.Option value="first-in-first-out">
												First In First Out
											</Select.Option>
											<Select.Option value="economics-of-scale">
												Economies of Scale
											</Select.Option>
											<Select.Option value="extended-replacement">
												Extended Replacement
											</Select.Option>
										</Select>
									</Space>

									{/* Cost/Count Toggle */}
									<Space>
										<span>View:</span>
										<Switch
											checkedChildren="Cost"
											unCheckedChildren="Count"
											checked={isYAxisReplCost}
											onChange={setIsYAxisReplCost}
										/>
									</Space>
								</div>
							</div>

							{/* Strategy Description */}
							<div
								style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}
							>
								{replacementStrategy === "first-in-first-out" &&
									"Replace assets as they reach end of useful life (default behavior)"}
								{replacementStrategy === "economics-of-scale" &&
									"Group similar asset replacements for bulk purchasing opportunities"}
								{replacementStrategy === "extended-replacement" &&
									"Prioritize only critical replacements, defer non-critical assets"}
							</div>
						</div>
						<div className="chart-container" style={{ height: "500px" }}>
							<PredSpendChart
								chartData={processedChartData}
								isYAxisReplCost={isYAxisReplCost}
								className="replacement-chart"
								showTotalLine={true}
								onColumnClick={handleColumnClick}
							/>
						</div>
					</Card>
				</div>
			) : (
				<div
					className="chart-section"
					style={{ paddingBottom: "16px", marginBottom: "12px" }}
				>
					<Card>
						<DFPEmpty description="No projected spending data available" />
					</Card>
				</div>
			)}

			{/* =============== INSIGHTS SECTION =============== */}
			{processedChartData && (
				<div className="insights-section">
					<Row gutter={[16, 16]}>
						<Col xs={24} lg={12}>
							<Card title="Key Insights" className="insights-card">
								<ul className="insights-list">
									<li>
										<strong>{processedChartData.assetTypes.length}</strong>{" "}
										different asset types tracked for replacement planning
									</li>
									<li>
										<strong>{processedChartData.yearsWithAssets.length}</strong>{" "}
										years of replacement data in{" "}
										<strong>{yearView}-year view</strong>
									</li>
									{processedChartData.hasEstimatedDates && (
										<li>
											Replacement years include <strong>estimated dates</strong>{" "}
											based on asset condition and useful life
										</li>
									)}
									<li>
										{yearView}-year budget allocation:{" "}
										<strong>
											{formatCurrency(processedChartData.totalBudget)}
										</strong>
									</li>
									<li>
										Current strategy:{" "}
										<strong>
											{replacementStrategy === "first-in-first-out" &&
												"First In First Out"}
											{replacementStrategy === "economics-of-scale" &&
												"Economics of Scale"}
											{replacementStrategy === "extended-replacement" &&
												"Extended Replacement"}
										</strong>
									</li>
								</ul>
							</Card>
						</Col>
						<Col xs={24} lg={12}>
							<Card
								title="Planning Recommendations"
								className="recommendations-card"
							>
								<ul className="recommendations-list">
									{replacementStrategy === "first-in-first-out" && (
										<>
											<li>
												Review assets past useful life for immediate replacement
												priority
											</li>
											<li>
												Plan budget allocation 2-3 years in advance for major
												replacements
											</li>
											<li>
												Monitor health scores regularly to adjust replacement
												timelines
											</li>
											<li>
												Consider switching to Economics of Scale for cost
												savings
											</li>
										</>
									)}
									{replacementStrategy === "economics-of-scale" && (
										<>
											<li>
												Leverage bulk purchasing for similar asset types in
												consolidated years
											</li>
											<li>
												Negotiate volume discounts with suppliers for grouped
												purchases
											</li>
											<li>
												Plan storage and installation capacity for larger
												batches
											</li>
											<li>
												Monitor asset condition to ensure deferred replacements
												remain viable
											</li>
										</>
									)}
									{replacementStrategy === "extended-replacement" && (
										<>
											<li>
												Focus maintenance efforts on non-critical assets to
												extend their life
											</li>
											<li>
												Develop clear criteria for defining "critical" asset
												replacements
											</li>
											<li>
												Plan for potential higher maintenance costs on deferred
												replacements
											</li>
											<li>
												Monitor deferred assets closely for unexpected failures
											</li>
										</>
									)}
								</ul>
							</Card>
						</Col>
					</Row>
				</div>
			)}

			{/* =============== SETTINGS MODAL =============== */}
			{user?.organization?.id && (
				<PredictiveSpendSettingsModal
					visible={isSettingsModalOpen}
					onClose={() => setIsSettingsModalOpen(false)}
					organizationId={user.organization.id}
					onSettingsUpdated={handleSettingsUpdated}
				/>
			)}
		</div>
	);
};

export { PredSpendTab };
